import { makeStyles, createStyles } from '@mui/styles';
import {
  ccfConversationsActions,
  getIsNewMessage,
  getNewAgentSelected,
  getSelectedAgent,
  getSelectedAgentType,
} from '../+state/ccf-conversations.slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  CONVERSATION_CONTAINER_FOOTER_HEIGHT,
  CONVERSATION_CONTAINER_REPLYBOX_HEIGHT,
} from '../utils/conversations.utils';
import {
  CcfAttachmentIcon,
  CcfBox,
  CcfButton,
  CcfTextField,
  CcfTypography,
  useTranslator,
} from '@nice-ccf/ui-controls';
import classes from './conversations-reply-app-container.module.scss';
import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatUnderlined,
  Send,
} from '@mui/icons-material';
import { useState } from 'react';
import { useMsal } from '@azure/msal-react';

const useStyles = makeStyles(() =>
  createStyles({
    chatContainer: {
      paddingInline: 8,
      height: 'auto',
      marginTop: 4,
      marginBottom: 8,
    },
    footerContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      paddingInline: 8,
      // marginTop: 4,
      marginBottom: 4,
      height: 'auto',
    },
    customButton: {
      minWidth: 30,
      marginRight: '0px',
    },
  })
);

/**
 * Component to be used for conversation reply
 * @example <ConversationsReplyContainer />
 * @returns
 */
export const ConversationsReplyAppContainer = () => {
  const [translate] = useTranslator();
  const styles = useStyles();
  const dispatch = useDispatch();
  const { accounts } = useMsal();
  const loginCredentialsFromSSO = accounts[0]?.name || ' ';
  const selectedAgent = useSelector(getSelectedAgent);
  const selectedAgentType = useSelector(getSelectedAgentType);
  const [message, setMessage] = useState('');
  const {
    updateSelectedAgent,
    updateAgentGroups,
    updateAgents,
    setIsNewMessage,
  } = ccfConversationsActions;
  const isNewMessage = useSelector(getIsNewMessage);
  const newAgentSelected = useSelector(getNewAgentSelected);

  /**
   * Handler for sending a message.
   * @example sendMessageHandler()
   */
  const sendMessageHandler = () => {
    const newMessage = {
      name: loginCredentialsFromSSO,
      status: 'available',
      text: message,
      timestamp: Date.now(),
      color: '#EE67AF',
    };

    if (!isNewMessage) {
      const updatedSelectedAgent = {
        ...selectedAgent,
        messages: [...selectedAgent.messages, newMessage],
      };

      dispatch(
        updateSelectedAgent({
          agent: updatedSelectedAgent,
          type: selectedAgentType,
        })
      ); //update selected agent in store

      const updateAction =
        selectedAgentType === 'group' ? updateAgentGroups : updateAgents;
      dispatch(
        updateAction({ agent: updatedSelectedAgent, type: 'updateMessage' })
      ); //update agents and agengroups in store
    } else {
      const newAgent = {
        name: newAgentSelected.name,
        status: newAgentSelected.status,
        messages: [newMessage],
        isFavorite: false,
        isMessageRead: true,
      };
      dispatch(updateAgents({ newAgent, type: 'add' }));
      dispatch(setIsNewMessage(false));
      dispatch(updateSelectedAgent({ agent: newAgent, type: 'direct' }));
      setTimeout(() => {
        document.getElementById('leftnavContainer')?.scroll({
          top: document.getElementById('leftnavContainer')?.scrollHeight, //scroll to the bottom of the element
          behavior: 'smooth', //auto, smooth, initial, inherit
        });
      }, 500);
    }
    setMessage(''); //clear the message box
  };

  /**
   * Handler for sending a message on click of enter button
   * @example enterMessageHandler()
   */
  const enterMessageHandler = (event: any) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      message.replace(/\s/g, '').length &&
      (newAgentSelected?.name || selectedAgent?.name)
    ) {
      event.preventDefault();
      sendMessageHandler();
    }
  };

  return (
    <>
      <section className={styles.chatContainer}>
        <CcfTextField
          id={`${selectedAgent?.name || 'new-message'}-textbox`}
          variant="outlined"
          placeholder={
            selectedAgent?.name
              ? `Chat with ${selectedAgent.name}`
              : 'New Message...'
          }
          fullWidth
          size="small"
          multiline
          InputProps={{
            rows: 3,
          }}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e: any) => enterMessageHandler(e)}
          maxRows={3}
        />
      </section>
      <footer className={styles.footerContainer}>
        <CcfBox className={classes.leftBtnGroup}>
          <CcfButton
            color="secondary"
            size="small"
            classes={{ root: styles.customButton }}
          >
            <FormatBold />
          </CcfButton>
          <CcfButton
            color="secondary"
            size="small"
            classes={{ root: styles.customButton }}
          >
            <FormatUnderlined />
          </CcfButton>
          <CcfButton
            color="secondary"
            size="small"
            classes={{ root: styles.customButton }}
          >
            <FormatItalic />
          </CcfButton>
          <CcfButton
            color="secondary"
            size="small"
            classes={{ root: styles.customButton }}
          >
            <FormatListBulleted />
          </CcfButton>
          <CcfButton
            color="secondary"
            size="small"
            classes={{ root: styles.customButton }}
            aria-label="Attachment"
          >
            <CcfAttachmentIcon />
          </CcfButton>
        </CcfBox>
        <CcfButton
          className={`${classes.sendBtn} ${
            message &&
            (isNewMessage ? newAgentSelected?.name : selectedAgent?.name)
              ? ''
              : classes.disabledSendBtn
          }`}
          onClick={sendMessageHandler}
        >
          <CcfTypography className={classes.sendBtnText}>
            {translate('send')}
          </CcfTypography>
          <Send style={{ fill: message ? 'white' : '#0000001a' }} />
        </CcfButton>
      </footer>
    </>
  );
};
